// cleanup_convert_to_typescript_file_GENERICS
// cleanup_remove_external_libraries_GENERICS

import React, { useState, useCallback, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { template } from 'lodash';

import { CaretRightIcon } from '@glu/icons-react';
import { Button, TERTIARY } from '@glu/buttons-react';
import { CheckboxRadio as Checkbox } from '@glu/form-components';

import useLocale from 'components/useLocale';
import { CLOSED, CURRENCY, OPEN, TYPES } from 'constants.js';

import MobileGridContext from '../logic/MobileGridContext';
import useStyles from './DataRow.styles';

const DataRow = ({
  bulkActionsEnabled,
  children,
  columns,
  forceOpen,
  isSelected,
  onBulkActionSelect,
  primaryColumns,
  rowData,
  rowId,
  rowIndex
}) => {
  const locale = useLocale();
  const {
    bulkActionSelect,
    bulkActionSvg,
    button,
    contentPanelHidden,
    contentPanelShown,
    expandoTh,
    leftAlign,
    mainCells,
    notDate,
    primaryRow,
    rightAlign,
    subCells,
    svg,
    svgOpen
  } = useStyles();

  const [isOpen, setOpen] = useState(false);

  const gridContext = useContext(MobileGridContext);

  const toggleOpen = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const handleCheckboxSelect = useCallback(
    (_name, _isChecked, isBlurEvent) => {
      if (isBlurEvent) return;
      onBulkActionSelect({ rowId, rowData });
    },
    [onBulkActionSelect, rowId, rowData]
  );

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
    }
  }, [forceOpen]);

  return (
    <>
      <tr className={primaryRow} data-qa={`primary-row-${rowIndex}`}>
        {bulkActionsEnabled && (
          <th rowSpan="2" className={`${bulkActionSelect} ${bulkActionSvg}`}>
            <Checkbox
              name={`checkbox_${rowId}`}
              htmlId={`checkbox_${rowId}`}
              checked={isSelected}
              onChange={handleCheckboxSelect}
              labelText={template(locale['common.grid.selectCheckbox'])({ id: rowId })}
              screenReaderLabel
            />
          </th>
        )}
        <th rowSpan="2" className={`${expandoTh} ${subCells}`}>
          {/* Only show this button when there are extra columns to see  */}
          {columns.length > 0 && (
            <Button
              dataQa="expand-subrow-button"
              className={button}
              type="button"
              onClick={toggleOpen}
              variant={TERTIARY}
            >
              <CaretRightIcon className={isOpen ? svgOpen : svg} />
            </Button>
          )}
        </th>
        {primaryColumns.map((col) => {
          // If there is a condition to check whether this should show or not
          const shouldShow = col.condition
            ? col.condition({
                data: rowData,
                gridContext,
                conditions: gridContext.columnConditions
              })
            : true;
          const shouldRightAlign = col.variant === CURRENCY;
          if (!shouldShow) return null;
          return (
            <td
              role="gridcell"
              className={classnames(
                mainCells,
                col.type !== TYPES.DATE ? notDate : '',
                shouldRightAlign ? rightAlign : leftAlign
              )}
              col-id={col.field}
              key={col.field}
              onClick={toggleOpen}
            >
              {col.cellDisplay ? col.cellDisplay({ rowData, column: col }) : rowData[col.field]}
            </td>
          );
        })}
      </tr>
      <tr>
        <td colSpan={primaryColumns.length}>
          <div
            className={isOpen ? contentPanelShown : contentPanelHidden}
            data-qa={`row-children-${isOpen ? OPEN : CLOSED}`}
          >
            {children}
          </div>
        </td>
      </tr>
    </>
  );
};

DataRow.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      cellDisplay: PropTypes.func,
      hide: PropTypes.bool,
      primary: PropTypes.bool
    })
  ),
  rowData: PropTypes.shape({}).isRequired,
  rowIndex: PropTypes.number.isRequired,
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  primaryColumns: PropTypes.arrayOf(
    PropTypes.shape({
      field: PropTypes.string.isRequired,
      cellDisplay: PropTypes.func,
      hide: PropTypes.bool,
      primary: PropTypes.bool
    })
  ).isRequired,
  children: PropTypes.node.isRequired,
  bulkActionsEnabled: PropTypes.bool.isRequired,
  isSelected: PropTypes.bool,
  onBulkActionSelect: PropTypes.func.isRequired,
  forceOpen: PropTypes.bool
};

DataRow.defaultProps = {
  columns: [],
  isSelected: false,
  forceOpen: false
};

export default DataRow;
